import { ICellRendererParams } from 'ag-grid-community'
import { light } from '@fortawesome/fontawesome-svg-core/import.macro'
import { Icon, Text, Tooltip } from 'src/components/ui'
import { zIndex } from 'src/utility/constants'
import { humanDateTime } from 'src/utility/time'
import { getNodeIcon } from '../../../opc-ua-hierarchy.utils'
import { TreeOpcUaNode } from '../../../opc-ua-hierarchy.types'

type Props = {
  params: ICellRendererParams<TreeOpcUaNode>
  errorNodes: string[]
}

export function GroupColumn({ params, errorNodes }: Props): JSX.Element {
  const nodeData = params.data
  // This shouldn't happen but just in case
  if (!nodeData) {
    return <Text>Unknown node</Text>
  }

  const { icon, className } = getNodeIcon(
    nodeData.class,
    nodeData.typeDefinition ?? undefined,
  )
  const isErrorNode = errorNodes.includes(nodeData.id)
  const isChildOfErrorNode = nodeData.path
    .slice(0, nodeData.path.length - 1)
    .some((id: string) => errorNodes.includes(id))
  return (
    <div className="relative flex items-center gap-xs">
      {isErrorNode && (
        <Tooltip
          zIndex={zIndex.modalLegendMenu}
          direction="bottom"
          render={() => {
            const errorMessage = nodeData.lastBrowseError ?? 'Unknown error'
            const timestamp = nodeData.lastBrowsedAt
              ? humanDateTime(nodeData.lastBrowsedAt)
              : undefined
            return (
              <Text>
                {timestamp ? `${timestamp}: ${errorMessage}` : errorMessage}
              </Text>
            )
          }}
        >
          <Icon
            icon={light('circle-exclamation')}
            className="text-icon-danger"
            size="regular"
          />
        </Tooltip>
      )}
      <Tooltip
        isOpen={isChildOfErrorNode ? undefined : false}
        zIndex={zIndex.modalLegendMenu}
        direction="bottom"
        render={() => (
          <Text>Node was last seen {humanDateTime(nodeData.lastSeenAt)}</Text>
        )}
      >
        <div className="flex items-center gap-xs">
          {!isErrorNode && (
            <Icon icon={icon} size="regular" className={className} />
          )}
          <Tooltip
            direction="right"
            isOpen={isChildOfErrorNode ? false : undefined}
            zIndex={zIndex.modalLegendMenu}
            className="max-w-[450px]"
            render={() => nodeData.nodeId}
          >
            <span>{nodeData.displayName ?? nodeData.browseName}</span>
          </Tooltip>
        </div>
      </Tooltip>
    </div>
  )
}
